'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { Pagination } from '@/components/catalog/Pagination';
import { CourseCard } from '@/components/catalog/CourseCard';
import { CategoryAccordion } from '@/components/catalog/CategoryAccordion';
import { CourseCardSkeletonGrid } from '@/components/catalog/CourseCardSkeletonGrid';
import { setQueryParam } from '@/lib/utils/url-params';
import { USER_DATA_UPDATED_EVENT } from '@/lib/constants/auth-events';
import { getUserData } from '@/helpers/local-storage';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { getTotalCourseDuration, getDuration } from '@/lib/utils/duration';
import { fetchCatalogData } from './actions';
import { hasPartnerTag } from '@/lib/constants/partner';
import { EXTERNAL_SOURCE_CONTENT_TYPES, PER_PAGE_TAB, PER_PAGE_DESKTOP, SEARCH_MIN_LENGTH, termNameToSearchTags } from '@/lib/utils/catalog-constants';
import { getEntryThumbnailUrl, CARD_FALLBACK_IMAGE } from '@/lib/utils/thumbnail-url';
import { useSearch } from '@/hooks/useSearch';
import { HamburgerIcon, CloseIcon } from '@/components/Icons/Icons';

const CONTENT_TYPE_FILTER_NAMES = ['Coding', 'Course', 'Demo', 'Explainer', 'Foundations'];

/** Normalize content type name for matching - "course" and "courses" both map to "course" */
function normalizeContentTypeForFilter(name: string): string {
  const l = (name || '').toLowerCase();
  return l === 'courses' ? 'course' : l;
}

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || '/academy';

/** Normalize a URL to a relative path, stripping basePath so Link doesn't double-prefix it. */
function toRelativeHref(url: string | undefined, isStandalone: boolean): string {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      let path = new URL(url).pathname;
      const base = BASE_PATH.endsWith('/') ? BASE_PATH.slice(0, -1) : BASE_PATH;
      if (path.startsWith(base + '/') || path === base) {
        path = path.slice(base.length) || '/';
      }
      return path;
    } catch {
      return url;
    }
  }
  return isStandalone ? `/content${url.startsWith('/') ? url : `/${url}`}` : `/courses${url.startsWith('/') ? url : `/${url}`}`;
}

interface CatalogPageContentProps {
  initialItems?: any[];
  initialCount?: number;
}

export function CatalogPageContent({ initialItems = [], initialCount = 0 }: CatalogPageContentProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [items, setItems] = useState<any[]>(initialItems);
  const [itemCount, setItemCount] = useState(initialCount);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(initialItems.length === 0);
  const [isInitialLoad, setIsInitialLoad] = useState(initialItems.length === 0);
  const [isUpdating, setIsUpdating] = useState(false); // Quick visual feedback for filter changes
  const [taxonomyData, setTaxonomiesData] = useState<any[]>([]);
  const [externalSourceNames, setExternalSourceNames] = useState<string[]>([]);
  const [externalSourceUids, setExternalSourceUids] = useState<string[]>([]);
  const [terms, setTerms] = useState<Map<string, string>>(new Map());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false); // For tablet/desktop collapse
  const [perPage, setPerPage] = useState(PER_PAGE_DESKTOP); // 6 for tab, 9 for desktop
  const [isFiltering, setIsFiltering] = useState(false); // Triggers re-render so useSearch runs after filter change

  // Tab view = 810px–1199px (2 cols × 3 rows = 6); desktop = 1200px+ (3 cols × 3 rows = 9)
  useEffect(() => {
    const tabQuery = window.matchMedia('(min-width: 810px) and (max-width: 1199px)');
    const update = () => setPerPage(tabQuery.matches ? PER_PAGE_TAB : PER_PAGE_DESKTOP);
    update();
    tabQuery.addEventListener('change', update);
    return () => tabQuery.removeEventListener('change', update);
  }, []);

  // Refs to prevent unnecessary re-renders and API calls
  const isFilteringRef = useRef(false);
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFilterStateRef = useRef<string>('');
  const lastSearchParamsRef = useRef<string>('');
  const lastPerPageRef = useRef<number>(PER_PAGE_DESKTOP);
  const prevSearchQueryRef = useRef<string | null>(null);
  const appliedContentTypeRef = useRef(false);

  // Reset pagination to page 1 when search text (q) changes (not on initial mount)
  useEffect(() => {
    const q = searchParams.get('q') ?? null;
    if (prevSearchQueryRef.current !== q) {
      const isInitialMount = prevSearchQueryRef.current === null;
      prevSearchQueryRef.current = q;
      if (!isInitialMount && searchParams.get('page')) {
        const params = new URLSearchParams(searchParams.toString());
        params.delete('page');
        const query = params.toString();
        router.replace(query ? `${pathname}?${query}` : pathname);
      }
    }
  }, [searchParams, pathname, router]);

  // Handle Filters - works with both search and catalog
  const handleFilters = useCallback(async (checkedFilters: Array<{ uid: string; parentUid: string }>) => {
    const filterKey = JSON.stringify(checkedFilters);
    
    // Only prevent if we're processing the EXACT same filter (not just any filter)
    if (isFilteringRef.current && lastFilterStateRef.current === filterKey) {
      return;
    }

    // Clear any pending filter calls
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    // Set flag to prevent useEffect from triggering duplicate calls; state triggers re-render for useSearch
    isFilteringRef.current = true;
    setIsFiltering(true);
    lastFilterStateRef.current = filterKey;

    // Immediate skeleton while catalog fetch or (with search) until useSearch finishes
    setIsUpdating(true);
    setLoading(true);

    let handsOffToSearch = false;
    try {
      const hasExternalSources = checkedFilters.some((f) => f.uid === 'external-sources');
      // Build new URL: current params + updated filter + externalUids when External sources checked
      const paramsAfterFilter = new URLSearchParams(searchParams.toString());
      if (checkedFilters.length > 0) {
        paramsAfterFilter.set('filter', JSON.stringify(checkedFilters).replace(/\\/g, ''));
        if (hasExternalSources && externalSourceUids.length > 0) {
          paramsAfterFilter.set('externalUids', externalSourceUids.join(','));
        } else {
          paramsAfterFilter.delete('externalUids');
        }
      } else {
        paramsAfterFilter.delete('filter');
        paramsAfterFilter.delete('externalUids');
      }
      paramsAfterFilter.delete('page');
      const queryAfterFilter = paramsAfterFilter.toString();
      router.replace(queryAfterFilter ? `${pathname}?${queryAfterFilter}` : pathname);

      const searchQuery = searchParams.get('q');
      const pageNum = 1;

      // With a search query, useSearch handles filtering; clear isFiltering after the URL update
      // but skip loading/isUpdating in finally — that ran too early and wiped the skeleton.
      if (searchQuery) {
        handsOffToSearch = true;
        filterTimeoutRef.current = setTimeout(() => {
          isFilteringRef.current = false;
          setIsFiltering(false);
        }, 100);
        return;
      }

      // No search query - use catalog filtering (externalUids from URL or state for expansion)
      const sortType = searchParams.get('sort') || 'New to Old';
      const filterParam = checkedFilters.length > 0 ? JSON.stringify(checkedFilters) : null;
      const externalUidsParam =
        searchParams.get('externalUids') ||
        (hasExternalSources && externalSourceUids.length > 0 ? externalSourceUids.join(',') : undefined);

      const coursesData = await fetchCatalogData({
        pageIndex: pageNum,
        filterParam,
        sortType,
        limit: perPage,
        externalUids: externalUidsParam,
      });

      setItems(coursesData[0]);
      setItemCount(coursesData[1]);
    } catch (error) {
      setItems([]);
      setItemCount(0);
    } finally {
      if (!handsOffToSearch) {
        setLoading(false);
        setIsInitialLoad(false);
        setIsUpdating(false);
        // Reset flag after a short delay to allow URL to update; setIsFiltering triggers re-render so useSearch runs
        filterTimeoutRef.current = setTimeout(() => {
          isFilteringRef.current = false;
          setIsFiltering(false);
        }, 300);
      } else {
        setIsInitialLoad(false);
        // loading / isUpdating cleared when searchResults arrive (or short-query branch) — see useSearch effects
      }
    }
  }, [searchParams, pathname, router, perPage, externalSourceUids]);

  // Handle Pagination - fetch catalog data for a specific page
  const handlePagination = useCallback(async (pageIndex: number) => {
    setIsUpdating(true); // Visual feedback for pagination
    try {
      const pageNum = isNaN(pageIndex) || pageIndex < 1 
        ? parseInt(searchParams.get('page') || '1') 
        : pageIndex;

      const coursesData = await fetchCatalogData({
        pageIndex: pageNum,
        filterParam: searchParams.get('filter'),
        sortType: searchParams.get('sort'),
        limit: perPage,
        externalUids: searchParams.get('externalUids') || undefined,
      });

      setItems(coursesData[0]);
      setItemCount(coursesData[1]);
    } catch (error) {
      setItems([]);
      setItemCount(0);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
      setIsUpdating(false);
    }
  }, [searchParams, perPage]);

  // Content Types = Coding/Course/Demo/Explainer/Foundations; rest = External sources.
  // Fetched via this app's own /api/taxonomies route (server-side) rather than calling the
  // Contentstack Management API directly from here, which used to ship a management-scoped
  // credential into the client bundle — see lib/utils/taxonomy.ts.
  useEffect(() => {
    const loadTaxonomies = async () => {
      try {
        const res = await fetch(`${BASE_PATH}/api/taxonomies`);
        if (!res.ok) throw new Error(`taxonomies fetch failed: ${res.status}`);
        const data = await res.json();
        // data.terms is { [uid]: name } for the "Content Type" taxonomy (the only one used here).
        const termsList = Object.entries(data.terms ?? {}).map(([uid, name]) => ({ uid, name: name as string }));

        const termsMap = new Map<string, string>(termsList.map((term) => [term.uid, term.name]));
        setTerms(termsMap);
        const nameLower = (name: string) => (name || '').toLowerCase();
        const allowedSet = new Set(CONTENT_TYPE_FILTER_NAMES.map(nameLower));
        const contentTypesChild = termsList
          .filter((term) => allowedSet.has(normalizeContentTypeForFilter(term.name)))
          .sort(
            (a, b) =>
              CONTENT_TYPE_FILTER_NAMES.map(nameLower).indexOf(normalizeContentTypeForFilter(a.name)) -
              CONTENT_TYPE_FILTER_NAMES.map(nameLower).indexOf(normalizeContentTypeForFilter(b.name))
          )
          .map((term) => ({ name: term.name, uid: term.uid }));
        const externalChild = termsList
          .filter((term) => !allowedSet.has(normalizeContentTypeForFilter(term.name)))
          .map((term) => ({ name: term.name, uid: term.uid }));
        setTaxonomiesData(
          contentTypesChild.length > 0
            ? [{ title: 'Content Types', uid: 'content_type', child: contentTypesChild }]
            : []
        );
        setExternalSourceNames(externalChild.map((t) => t.name));
        setExternalSourceUids(externalChild.map((t) => t.uid));
      } catch {
        // Error fetching taxonomy — leave filters empty rather than blocking the page.
      }
    };
    loadTaxonomies();
  }, []);

  // When landing from "All courses" (featured) with ?contentType=course or ?contentType=courses, apply Course filter and show results
  useEffect(() => {
    const contentType = searchParams.get('contentType');
    const normalizedContentType = normalizeContentTypeForFilter(contentType ?? '');
    if (normalizedContentType !== 'course' || appliedContentTypeRef.current || taxonomyData.length === 0) return;

    const contentTypesTax = taxonomyData.find(
      (t) => t.uid === 'content_type' || t.title === 'Content Types'
    );
    const children = contentTypesTax?.child || [];
    const courseTerm = children.find(
      (c: { name: string; uid: string }) => normalizeContentTypeForFilter(c.name) === 'course'
    );
    if (!courseTerm) return;

    appliedContentTypeRef.current = true;
    const filter = [{ uid: courseTerm.uid, parentUid: 'content_type' }];
    const params = new URLSearchParams(searchParams.toString());
    params.delete('contentType');
    params.set('filter', JSON.stringify(filter));
    params.delete('page');
    router.replace(`${pathname}?${params.toString()}`);
  }, [searchParams, pathname, router, taxonomyData]);

  // Get user data on mount and when user logs in/out (e.g. OAuth, partner SSO)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncUser = () => {
      setUser(getUserData());
    };

    syncUser();
    window.addEventListener(USER_DATA_UPDATED_EVENT, syncUser);
    return () => window.removeEventListener(USER_DATA_UPDATED_EVENT, syncUser);
  }, []);

  // Mobile filter toggle
  const toggleMobileFilter = (open: boolean) => setIsDropdownOpen(open);

  // Get filters from URL for search (expand external-sources to actual content type UIDs from URL or fallback)
  const getFilterUids = useCallback((): string[] => {
    const filterParam = searchParams.get('filter');
    if (!filterParam) return [];
    
    try {
      const filters = JSON.parse(filterParam);
      const externalUidsParam = searchParams.get('externalUids');
      const externalUids = externalUidsParam
        ? externalUidsParam.split(',').map((u) => u.trim()).filter(Boolean)
        : EXTERNAL_SOURCE_CONTENT_TYPES;
      const uids: string[] = [];
      filters.forEach((f: { uid: string; parentUid: string }) => {
        if (f.uid === 'external-sources') {
          uids.push(...externalUids);
        } else {
          uids.push(f.uid);
        }
      });
      return [...new Set(uids)];
    } catch {
      return [];
    }
  }, [searchParams]);

  // Use React Query for search with caching
  const searchQueryParam = searchParams.get('q');
  const searchPage = parseInt(searchParams.get('page') || '1');
  const filterUids = getFilterUids();
  // Search API filters by tag name, not taxonomy term UID, so convert; external source UIDs may already be tag names.
  const searchFilterTags = filterUids.flatMap((uid) => {
    const termName = terms.get(uid);
    if (termName) return termNameToSearchTags(termName);
    return [uid];
  });
  const uniqueSearchTags = [...new Set(searchFilterTags)];

  const { data: searchResults, isLoading: isSearchLoading } = useSearch({
    query: searchQueryParam || '',
    filters: uniqueSearchTags,
    page: searchPage,
    perPage,
    // Partner users: include partner-specific content. Non-partner / logged-out: filter to is_partner_specific=false
    excludePartnerSpecific: !hasPartnerTag(user),
    enabled:
      !!searchQueryParam &&
      searchQueryParam.length >= SEARCH_MIN_LENGTH &&
      !isFiltering &&
      (filterUids.length === 0 || terms.size > 0 || filterUids.some((u) => EXTERNAL_SOURCE_CONTENT_TYPES.includes(u))),
  });

  // Update items when search results change (search only runs when query length >= SEARCH_MIN_LENGTH)
  useEffect(() => {
    if (!searchQueryParam) return;
    if (searchQueryParam.length < SEARCH_MIN_LENGTH) {
      setItems([]);
      setItemCount(0);
      setLoading(false);
      setIsUpdating(false);
      return;
    }
    if (searchResults?.records?.page) {
      // Map search results to catalog item format (Academy 1.0: thumbnail from image/thumbnail/og, user_data preserved)
      const mappedItems = searchResults.records.page.map((item: any) => {
        const url = getEntryThumbnailUrl(item);
        return {
          ...item,
          title: item.title || item.highlight?.title || '',
          thumbnail: url ? { url } : { url: '' },
          url: item.url || '',
          is_standalone: item.is_standalone ?? false,
          is_partner_specific: item.is_partner_specific == "true" ? true : false,
          taxonomies: item.taxonomies || [],
          lesson_details: item.lesson_details,
          video_lesson: item.video_lesson,
          time_to_read: item.time_to_read,
          duration: item.duration,
          user_data: item.user_data,
        };
      });

      setItems(mappedItems);
      
      // Calculate total item count from search API response
      const totalResultCount = searchResults.info?.page?.total_result_count;
      const totalPages = searchResults.info?.page?.total_pages;
      const perPage = searchResults.info?.page?.per_page || 9;
      
      if (totalResultCount) {
        setItemCount(totalResultCount);
      } else if (totalPages) {
        setItemCount(totalPages * perPage);
      } else {
        setItemCount(mappedItems.length);
      }
    } else {
      setItems([]);
      setItemCount(0);
    }
    
    setLoading(false);
    setIsInitialLoad(false);
    setIsUpdating(false);
  }, [searchResults, searchQueryParam]);

  // Handle search loading state
  useEffect(() => {
    if (searchQueryParam && isSearchLoading) {
      setIsUpdating(true);
      setLoading(true);
    }
  }, [isSearchLoading, searchQueryParam]);

  // Fetch data when searchParams or user changes - App Router pattern
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentParams = searchParams.toString();
    const filterParam = searchParams.get('filter');
    let filters: any[] = [];
    if (filterParam) {
      try {
        filters = JSON.parse(filterParam);
      } catch (error) {
        filters = [];
      }
    }
    const currentFilterKey = JSON.stringify(filters);
    
    // If handleFilters is processing, skip useEffect to prevent race conditions
    if (isFilteringRef.current) {
      return;
    }
    
    // Skip if params and perPage haven't changed (but allow initial load)
    if (
      lastSearchParamsRef.current === currentParams &&
      lastPerPageRef.current === perPage &&
      items.length > 0
    ) {
      return;
    }
    lastSearchParamsRef.current = currentParams;
    lastPerPageRef.current = perPage;

    const fetchData = async () => {
      // useSearch fetches automatically when the query param changes
      const urlSearchQuery = searchParams.get('q');
      if (urlSearchQuery) {
        return;
      }

      const page = searchParams.get('page');

      if (page && !filters?.length) {
        await handlePagination(parseInt(page));
      } else if (filters?.length) {
        lastFilterStateRef.current = currentFilterKey;
        await handleFilters(filters);
      } else if (!page) {
        await handlePagination(1);
      }
    };

    fetchData();

    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.tags?.join(','), perPage]);

  // Handle pagination when user with partner tag loads and no partner-specific courses are shown
  useEffect(() => {
    if (
      hasPartnerTag(user) &&
      items.length > 0 &&
      !items.some((el) => el?.is_partner_specific)
    ) {
      handlePagination(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Academy 1.0: catalog uses last taxonomy term_uid; search uses last tag; lookup in content_type terms map (uid -> name)
  const getCoursetype = (item: any) => {
    // let term = terms.get(item.taxonomies?.[item.taxonomies.length - 1]?.term_uid);
    // return term ? term : item.is_standalone ? 'Content' : 'Video Course';
    const lastTermUid = item?.taxonomies?.[item.taxonomies?.length - 1]?.term_uid ?? item?.tags?.[item.tags?.length - 1];
    const termName = lastTermUid ? terms.get(lastTermUid) : undefined;
    return termName ?? (item?.is_standalone ? 'Content' : 'Course');
  };

  const getDisplayItems = () => {
    // Show skeleton on initial load or when updating (pagination, filter change)
    if ((loading && isInitialLoad) || isUpdating) {
      return <CourseCardSkeletonGrid count={perPage} />;
    }

    if (items.length === 0 && !loading) {
      return (
        <div className="text-center py-12">
          <p className="cs-s-body text-text-default-medium">No courses found.</p>
        </div>
      );
    }

    // Filter out partner-specific courses if user doesn't have partner tag
    const filteredItems = items.filter((el) => {
      if (!hasPartnerTag(user) && el?.is_partner_specific) {
        return false;
      }
      return true;
    });

    const maxItemsPerPage = perPage;
    const displayItems = filteredItems.slice(0, maxItemsPerPage);
    return (
      <div className="bg-surface-1 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4 lg:gap-8">
            {displayItems.map((el, i) => {
              const rawDuration =
                el.duration != null && el.duration !== ''
                  ? (typeof el.duration === 'string' ? el.duration : getDuration(el.duration))
                  : el.is_standalone
                    ? getDuration(
                        el?.lesson_details?.video_lesson?.[0]?.duration ||
                          el?.video_lesson?.[0]?.duration ||
                          el.time_to_read
                      )
                    : getTotalCourseDuration(el);
              const duration = rawDuration ?? '';
              const courseType = terms.size > 0 ? getCoursetype(el) : (el?.is_standalone ? 'Content' : 'Course');
              const href = toRelativeHref(el?.url, el?.is_standalone ?? false);
              const thumbnailUrl = getEntryThumbnailUrl(el) || CARD_FALLBACK_IMAGE;

              return (
                <CourseCard
                  key={`${el?.uid ?? el?.entry_uid ?? ''}-${i}-${thumbnailUrl}`}
                  title={el.title}
                  duration={duration}
                  backgroundImage={thumbnailUrl}
                  href={href}
                  type={courseType}
                />
              );
            })}
        </div>
        <div className="w-full flex justify-center mt-16">
          <Pagination
            key={`${searchParams.get('filter') ?? ''}-${searchParams.get('q') ?? ''}`}
            pageCount={itemCount}
            limit={perPage}
            currentPageFromUrl={searchQuery ? parseInt(searchParams.get('page') || '1', 10) : undefined}
            action={searchQuery
              ? async (pageIndex: number) => {
                  if (isNaN(pageIndex)) return;
                  setIsUpdating(true);
                  const params = new URLSearchParams(searchParams.toString());
                  if (pageIndex > 1) params.set('page', String(pageIndex));
                  else params.delete('page');
                  const query = params.toString();
                  router.replace(query ? `${pathname}?${query}` : pathname);
                  // isUpdating cleared when search results arrive (useSearch → searchResults effect)
                }
              : async (pageIndex: number) => {
                  await handlePagination(pageIndex);
                  const params = new URLSearchParams(searchParams.toString());
                  if (pageIndex > 1) params.set('page', String(pageIndex));
                  else params.delete('page');
                  const query = params.toString();
                  router.replace(query ? `${pathname}?${query}` : pathname);
                }}
          />
        </div>
      </div>
    );
  };

  const filterParam = searchParams.get('filter');
  const searchQuery = searchParams.get('q');
  
  // Check if filters are selected
  const hasFilters = (() => {
    if (!filterParam) return false;
    try {
      const filters = JSON.parse(filterParam);
      return Array.isArray(filters) && filters.length > 0;
    } catch {
      return false;
    }
  })();

  // Show results count when filters are active OR when search query exists
  const showResultsCount = hasFilters || !!searchQuery;

  return (
    <div className="section bg-surface-1">
          {!searchQuery && <h1 className="sr-only">Course Catalog</h1>}
          {/* Search Results Header */}
          {searchQuery && (
            <div className="pb-8 border-b border-border-light mb-8 min-w-0">
              <div className="flex flex-col tab:flex-row tab:items-end tab:justify-between gap-3 min-w-0">
                <h1 className="cs-h3 tab:cs-h1-compact desktop:h1-standard min-w-0 break-words">
                  <span className="text-text-default-light">Results for</span>{' '}
                  <span className="text-text-default-strong break-words">{searchQuery}</span>
                </h1>
                <a
                  href={`${process.env.NEXT_PUBLIC_DOCS_BASE_URL}/docs/?q=${encodeURIComponent(searchQuery)}&filters=Docs`}
                  className="py-0.5 inline-flex items-center gap-0.5 additional-type-s-button font-bold min-w-0 break-words"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="break-words">See <strong>&quot;{searchQuery}&quot;</strong> results on Docs</span>
                  <MaterialIcons
                    iconName="open_in_new"
                    className="text-icon-subtle flex-shrink-0 !text-[20px]"
                  />
                </a>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-8 tab:flex-row relative">
            {/* Filter icon button when collapsed - visible on tablet only */}
            {isFilterCollapsed && (
              <div className="hidden tab:block desktop:hidden">
                <button
                  onClick={() => setIsFilterCollapsed(false)}
                  className="flex items-center justify-center p-2 text-text-default-medium hover:text-text-default-strong hover:bg-surface-2 rounded transition-colors w-auto h-fit"
                  aria-label="Expand filters"
                >
                  <MaterialIcons iconName="tune" className="h-5 w-5 rotate-90 text-icon-subtle cursor-pointer" />
                </button>
              </div>
            )}

            {/* Left Navigation - Filters */}
            <div className={`w-full tab:transition-all tab:duration-200 tab:ease-in-out relative ${
              isFilterCollapsed ? 'tab:hidden desktop:block desktop:w-1/4 desktop:max-w-[240px]' : 'tab:w-1/4 tab:max-w-[240px]'
            }`}>
              {/* Filter toggle button - mobile only (below tab breakpoint) */}
              <div className="flex items-center tab:hidden">
                <button
                  className="flex items-center gap-1 text-text-default-medium hover:text-text-default-strong transition-colors"
                  data-bs-auto-close="false"
                  data-swiftype-index="false"
                  aria-label={isDropdownOpen ? 'Collapse filters' : 'Expand filters'}
                  onClick={() => toggleMobileFilter(!isDropdownOpen)}
                >
                  {isDropdownOpen ? (
                    <>
                      <MaterialIcons 
                        iconName="chevron_left" 
                        className="!text-[20px] flex-shrink-0 text-icon-subtle cursor-pointer" 
                      />
                      <span className="additional-type-s-button">Collapse filter</span>
                    </>
                  ) : (
                    <>
                      <MaterialIcons
                        iconName="tune"
                        className="!text-[20px] flex-shrink-0 text-icon-subtle cursor-pointer rotate-90"
                      />
                      <span className="additional-type-s-button">Filters</span>
                    </>
                  )}
                </button>
              </div>
              {/* Collapse filter button when expanded - visible on tablet only */}
              {!isFilterCollapsed && (
                <button
                  onClick={() => setIsFilterCollapsed(true)}
                  className="hidden tab:flex desktop:hidden items-center gap-1 mb-4 text-text-default-medium hover:text-text-default-strong transition-colors w-full"
                  aria-label="Collapse filters"
                >
                  <MaterialIcons 
                    iconName="chevron_left" 
                    className="!text-[20px] flex-shrink-0 text-icon-subtle cursor-pointer" 
                  />
                  <span className="additional-type-s-button">Collapse filter</span>
                </button>
              )}

              <div
                className={`left-category-wrapper bg-surface-1 tab:sticky tab:top-18 tab:max-h-[calc(100vh-2rem)] tab:overflow-y-auto tab:relative tab:w-auto tab:h-auto tab:shadow-none ${
                  // Mobile: show when dropdown is open
                  isDropdownOpen 
                    ? 'block relative w-full h-auto overflow-y-auto py-6' 
                    // Tablet: show when not collapsed, hide when collapsed
                    : isFilterCollapsed
                      ? 'hidden tab:hidden desktop:block'
                      : 'hidden tab:block desktop:block'
                }`}
              >

                {/* Filter Results Count - smooth collapse/expand - Show when filters are active OR search query exists */}
                <div 
                  className={`grid transition-all duration-200 ease-in-out ${showResultsCount ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                  <div className="overflow-hidden">
                    <div className="mb-8 flex flex-col gap-1">
                      <h4 className="cs-h4 text-text-default-strong">
                        {searchQuery ? 'Filter results' : 'Filter results'} ...
                      </h4>
                      <p className="cs-caption flex flex-wrap items-baseline gap-1">
                        <span>Currently</span> <strong>{itemCount}</strong> <span>{itemCount === 1 ? 'result' : 'results'}</span>
                      </p>
                    </div>
                  </div>
                </div>

                
                {taxonomyData.map((el, key) => {
                  return (
                    <CategoryAccordion
                      title={el.title}
                      data={el.child.map((child: any) => child.name)}
                      key={key}
                      uidList={el.child.map((child: any) => child.uid)}
                      parentUid={el.uid}
                      action={handleFilters}
                      filterParamFromUrl={searchParams.get('filter')}
                      externalSourceNames={key === 0 ? externalSourceNames : undefined}
                    />
                  );
                })}
              </div>
            </div>

            {/* Right Content Area */}
            <div className={`flex-1 tab:transition-all tab:duration-200 tab:ease-in-out tab:min-w-0`}>
              {items?.length > 0 || loading ? getDisplayItems() : (
                <div className="text-center py-12">
                  <p className="cs-s-body">No courses found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
  );
}

